import type { Card, Stat2 } from "src/types/statsperform/matchStats";

const STAT_TYPES = [
	"goals",
	"possessionPercentage",
	"totalScoringAtt",
	"ontargetScoringAtt",
	"cornerTaken",
	"totalOffside",
	"accuratePass",
	"totalPass",
	"fkFoulLost",
] as const;
type StatType = (typeof STAT_TYPES)[number];

const isStatType = (type: string): type is StatType => STAT_TYPES.includes(type as StatType);

const getMatchStatsFromStat2 = (stats?: Stat2[]) => {
	if (!stats) return {} as { [K in StatType]: string };
	return stats.reduce<{ [K in StatType]: string }>(
		(acc, curr) => {
			if (!isStatType(curr.type)) return acc;
			acc[curr.type as StatType] = curr.value;
			return acc;
		},
		{} as { [K in StatType]: string },
	);
};

const getCards = (cards: Card[] | undefined, contestantId: string): [number, number] => {
	if (!cards) return [0, 0];

	const yellowCards = cards.filter(
		(card) => card.contestantId === contestantId && card.type === "YC",
	);
	const redCards = cards.filter((card) => card.contestantId === contestantId && card.type === "RC");

	return [yellowCards.length, redCards.length];
};

const val = (v?: string) => v ?? "0";
const pct = (v: string) => `${Math.round(Number(v ?? 0))}%`;
const passPct = (accurate: string, total: string) =>
	`${accurate} (${Math.round((Number(accurate) / Number(total)) * 100)}%)`;

type MatchStats = ReturnType<typeof getMatchStatsFromStat2>;

type StatRowData = {
	label: string;
	homeValue: string;
	awayValue: string;
};

type StatRowConfig = {
	label: string;
	getValue: (params: {
		homeStats: MatchStats;
		awayStats: MatchStats;
		yellowHome: number;
		redHome: number;
		yellowAway: number;
		redAway: number;
	}) => {
		homeValue: string;
		awayValue: string;
	};
};

const STAT_ROW_CONFIGS: StatRowConfig[] = [
	{
		label: "Ball Possession",
		getValue: ({ homeStats, awayStats }) => ({
			homeValue: pct(homeStats.possessionPercentage),
			awayValue: pct(awayStats.possessionPercentage),
		}),
	},
	{
		label: "Total Attempts",
		getValue: ({ homeStats, awayStats }) => ({
			homeValue: val(homeStats.totalScoringAtt),
			awayValue: val(awayStats.totalScoringAtt),
		}),
	},
	{
		label: "Attempts on Target",
		getValue: ({ homeStats, awayStats }) => ({
			homeValue: val(homeStats.ontargetScoringAtt),
			awayValue: val(awayStats.ontargetScoringAtt),
		}),
	},
	{
		label: "Corners",
		getValue: ({ homeStats, awayStats }) => ({
			homeValue: val(homeStats.cornerTaken),
			awayValue: val(awayStats.cornerTaken),
		}),
	},
	{
		label: "Offsides",
		getValue: ({ homeStats, awayStats }) => ({
			homeValue: val(homeStats.totalOffside),
			awayValue: val(awayStats.totalOffside),
		}),
	},
	{
		label: "Passes Completed",
		getValue: ({ homeStats, awayStats }) => ({
			homeValue: passPct(homeStats.accuratePass, homeStats.totalPass),
			awayValue: passPct(awayStats.accuratePass, awayStats.totalPass),
		}),
	},
	{
		label: "Fouls Committed",
		getValue: ({ homeStats, awayStats }) => ({
			homeValue: val(homeStats.fkFoulLost),
			awayValue: val(awayStats.fkFoulLost),
		}),
	},
	{
		label: "Yellow Cards",
		getValue: ({ yellowHome, yellowAway }) => ({
			homeValue: val(yellowHome?.toString()),
			awayValue: val(yellowAway?.toString()),
		}),
	},
	{
		label: "Red Cards",
		getValue: ({ redHome, redAway }) => ({
			homeValue: val(redHome?.toString()),
			awayValue: val(redAway?.toString()),
		}),
	},
];

const createStatRows = (params: {
	homeStats: MatchStats;
	awayStats: MatchStats;
	yellowHome: number;
	redHome: number;
	yellowAway: number;
	redAway: number;
}): StatRowData[] =>
	STAT_ROW_CONFIGS.map((config) => ({
		label: config.label,
		...config.getValue(params),
	}));

export { createStatRows, getCards, getMatchStatsFromStat2 };
